<template>
  <span :class="avatarClass" :style="sizeStyle">
    <img
      v-if="(src || srcSet) && !hasLoadError"
      :src="src"
      :alt="alt"
      :srcset="srcSet"
      :style="fitStyle"
      @error="handleError"
    />
    <jd-icon v-else-if="icon">
      <component :is="icon" />
    </jd-icon>
    <slot v-else />
  </span>
</template>

<script lang="ts" setup>
import { computed, inject, ref, watch } from 'vue'
import { JdIcon } from '@jdesign-vue/components/icon'
import { useNamespace } from '@jdesign-vue/hooks'
import { addUnit, isNumber, isString } from '@jdesign-vue/utils'
import { avatarEmits, avatarProps } from './avatar'
import { avatarGroupContextKey } from './constants'

import type { CSSProperties } from 'vue'

defineOptions({
  name: 'JdAvatar',
})

const props = defineProps(avatarProps)
const emit = defineEmits(avatarEmits)

const avatarGroupContext = inject(avatarGroupContextKey, undefined)
const _size = computed(() => avatarGroupContext?.size || props.size)

const ns = useNamespace('avatar')

const hasLoadError = ref(false)

const avatarClass = computed(() => {
  const { icon, shape } = props
  const classList = [ns.b()]
  if (isString(_size.value)) classList.push(ns.m(_size.value))
  if (icon) classList.push(ns.m('icon'))
  if (shape) classList.push(ns.m(shape))
  return classList
})

const sizeStyle = computed(() => {
  return isNumber(_size.value)
    ? (ns.cssVarBlock({
        size: addUnit(_size.value) || '',
      }) as CSSProperties)
    : undefined
})

const fitStyle = computed<CSSProperties>(() => ({
  objectFit: props.fit,
}))

// need reset hasLoadError to false if src changed
watch(
  () => props.src,
  () => (hasLoadError.value = false)
)

function handleError(e: Event) {
  hasLoadError.value = true
  emit('error', e)
}
</script>
